// Messages' places in the apps (PMU-233, PMU-234): a Messages tab with the unread count in the
// parent app (a parent's own side only) and the staff app (a conversation opens under it,
// /messages/<id>), and a Messages page in the console with the inbox beside the open conversation,
// and for the office, safeguarding.
import { useEffect, useState } from "react";
import { navigate, usePath, useQuery, type UiPart } from "@vavr/screens/shared";
import { G, useGuardian } from "@vavr/screens/guardian";
import { T, useTeacher } from "@vavr/screens/teacher";
import { useConsole } from "@vavr/screens/console";
import { Button, EmptyState } from "@vavr/ui";
import { ConversationView, InboxView, MessagesWatch, PrefsButton, unreadCount } from "./screens.tsx";
import { safetyNav } from "../safety/page.tsx";

/** The conversation id under a Messages tab's path, if one is open. */
const openId = (path: string, base: string) => path.startsWith(`${base}/messages/`) ? decodeURIComponent(path.slice(`${base}/messages/`.length).split("/")[0]!) : null;

function GuardianMessages() {
  const path = usePath();
  const { overview } = useGuardian();
  const tz = overview.sites[0]?.site.tz;
  const id = openId(path, G);
  return id ? <ConversationView id={id} tz={tz} /> : <InboxView parents tz={tz} open={(t) => navigate(`${G}/messages/${t}`)} />;
}

/** The top bar on the Messages inbox: settings (language; for staff, office hours). A conversation has the back arrow instead. */
function GuardianTopBar() {
  return openId(usePath(), G) ? null : <PrefsButton icon staffHours={false} />;
}
function StaffTopBar() {
  const { site } = useTeacher();
  return openId(usePath(), T) ? null : <PrefsButton icon staffHours tz={site.tz} />;
}

function StaffMessages() {
  const path = usePath();
  const { site } = useTeacher();
  const id = openId(path, T);
  return id ? <ConversationView id={id} tz={site.tz} />
    : <InboxView staffHours tz={site.tz} startFamily={site.org_kind === "operator" ? null : site.id} open={(t) => navigate(`${T}/messages/${t}`)} />;
}

/**
 * The console's Messages page: the inbox beside the open conversation, filling the page. Reports
 * and safeguarding are on their own page (Safety reports). `?open=<id>` opens a conversation (from
 * the bell, search or a report).
 */
function ConsoleMessages() {
  const { site, can } = useConsole();
  const [open, setOpen] = useState<string | null>(null);
  const wanted = new URLSearchParams(useQuery()).get("open");
  useEffect(() => { if (wanted) setOpen(wanted); }, [wanted]);
  const school = site.org_kind !== "operator";
  return (
    <div className={`pmm-console pmm-console--page${open ? " pmm-console--selected" : ""}`}>
      {/* A bus company's depot has no families to message. */}
      <div className="pmm-console__list"><InboxView compact settings staffHours selectedId={open} tz={site.tz} startFamily={can("messages:families") && school ? site.id : null} open={setOpen} /></div>
      <section className="pmm-console__open" aria-label="Conversation">{open ? <><div className="pmm-console__back"><Button size="sm" variant="ghost" icon="chevron-left" onClick={() => setOpen(null)}>Back to inbox</Button></div><ConversationView key={open} id={open} tz={site.tz} /></> : <EmptyState icon="message" title="Choose a conversation" description="Read school updates, connect with families, or talk with your team. Select a conversation from the inbox to get started." />}</section>
    </div>
  );
}

export const messagesUi: UiPart = {
  id: "messages",
  slots: {
    "guardian.tab": [{ id: "messages", label: "Messages", icon: "message", path: "messages", title: "Messages", count: unreadCount, topBar: () => <GuardianTopBar />, render: () => <GuardianMessages /> }],
    "guardian.wrap": [{ id: "messages", wrap: (children) => <MessagesWatch parents>{children}</MessagesWatch> }],
    // Before Me; staff without staff messages (none today) would not see the tab.
    "staff.tab": [{ id: "messages", label: "Messages", icon: "message", path: "messages", title: "Messages", permission: "messages:staff", count: unreadCount, topBar: () => <StaffTopBar />, render: () => <StaffMessages /> }],
    "staff.wrap": [{ id: "messages", wrap: (children) => <MessagesWatch>{children}</MessagesWatch> }],
    "console.nav": [{ id: "messages", label: "Messages", icon: "message", path: "messages", title: "Messages", permission: "messages:staff", group: "Daily", fullBleed: true, render: () => <ConsoleMessages /> }, safetyNav],
  },
};
